// API 相关工具函数

/**
 * API 基础配置
 */
export const API_CONFIG = {
  BASE_URL: (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || process.env.REACT_APP_API_BASE_URL || '',
  TIMEOUT: 30000, // 30秒
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000 // 1秒
} as const;

/**
 * API 响应类型定义
 */
export interface ApiError {
  name: string;
  code: string;
  message: string;
  details?: any;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
}

/**
 * HTTP 方法类型
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * 请求配置接口
 */
export interface RequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, any>;
  timeout?: number;
  retries?: number;
  signal?: AbortSignal;
}

/**
 * 自定义 API 客户端类
 */
export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;

  constructor(config: { baseUrl?: string; timeout?: number } = {}) {
    // 优先使用传入的 baseUrl，其次使用配置的 BASE_URL
    let base = config.baseUrl || API_CONFIG.BASE_URL;
    
    // 如果 base 是相对路径或为空，在浏览器环境下自动补全当前域名
    if (!base || base.startsWith('/')) {
      const global = globalThis as any;
      if (typeof global.window !== 'undefined' && global.window.location) {
        base = global.window.location.origin + (base || '');
      } else {
        // 非浏览器环境（如 Node.js 测试）下的默认值
        base = 'http://localhost';
      }
    }
    
    this.baseUrl = base;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    this.timeout = config.timeout || API_CONFIG.TIMEOUT;
  }

  /**
   * 设置认证 Token
   */
  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  /**
   * 移除认证 Token
   */
  removeAuthToken() {
    delete this.defaultHeaders['Authorization'];
  }

  /**
   * 构建完整 URL
   */
  private buildUrl(endpoint: string, params?: Record<string, any>): string {
    const url = new URL(endpoint, this.baseUrl);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    
    return url.toString();
  }

  /**
   * 发送 HTTP 请求
   */
  private async request<T = any>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      params,
      timeout = this.timeout,
      signal
    } = config;

    const url = this.buildUrl(endpoint, params);
    
    const requestHeaders = { ...this.defaultHeaders, ...headers };
    
    // 准备请求体
    let requestBody: string | undefined;
    if (body) {
      if (body instanceof FormData) {
        delete requestHeaders['Content-Type']; // 让浏览器自动设置
        requestBody = undefined;
      } else {
        requestBody = JSON.stringify(body);
      }
    }

    // 创建 AbortController 用于超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    // 如果外部提供了 signal，也要监听它
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body instanceof FormData ? body : (requestBody ?? null),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // 处理响应
      const responseData = await this.parseResponse<T>(response);
      
      return responseData;

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: {
              name: 'TimeoutError',
              code: 'TIMEOUT',
              message: '请求超时，请稍后重试'
            }
          };
        }
        
        return {
          success: false,
          error: {
            name: 'NetworkError',
            code: 'NETWORK_ERROR',
            message: '网络错误，请检查网络连接',
            details: error.message
          }
        };
      }
      
      return {
        success: false,
        error: {
          name: 'UnknownError',
          code: 'UNKNOWN_ERROR',
          message: '未知错误'
        }
      };
    }
  }

  /**
   * 解析响应数据
   */
  private async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      const text = await response.text();
      
      // 尝试解析为 JSON
      let data: any;
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }

      if (response.ok) {
        // 成功响应
        if (data && typeof data === 'object' && 'success' in data) {
          return data as ApiResponse<T>;
        } else {
          return {
            success: true,
            data: data as T
          };
        }
      } else {
        // 错误响应
        if (data && typeof data === 'object' && 'error' in data) {
          return data as ApiResponse<T>;
        } else {
          return {
            success: false,
            error: {
              name: 'HttpError',
              code: `HTTP_${response.status}`,
              message: data?.message || response.statusText || '请求失败'
            }
          };
        }
      }
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'ParseError',
          code: 'PARSE_ERROR',
          message: '响应数据解析失败',
          details: error
        }
      };
    }
  }

  /**
   * GET 请求
   */
  async get<T = any>(endpoint: string, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POST 请求
   */
  async post<T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body: data });
  }

  /**
   * PUT 请求
   */
  async put<T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body: data });
  }

  /**
   * DELETE 请求
   */
  async delete<T = any>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * PATCH 请求
   */
  async patch<T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body: data });
  }

  /**
   * 文件上传
   */
  async upload<T = any>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // 不要设置 Content-Type，让浏览器自动设置
      }
    });
  }

  /**
   * 批量文件上传
   */
  async uploadMultiple<T = any>(endpoint: string, files: File[], additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    
    files.forEach((file) => {
      formData.append(`files`, file);
    });
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // 不要设置 Content-Type，让浏览器自动设置
      }
    });
  }
}

/**
 * 创建默认的 API 客户端实例
 */
export const apiClient = new ApiClient();

/**
 * 带 Token 的 API 请求函数
 */
export const apiWithAuth = {
  /**
   * 设置认证 Token
   */
  setToken: (token: string) => apiClient.setAuthToken(token),
  
  /**
   * 移除认证 Token
   */
  removeToken: () => apiClient.removeAuthToken(),
  
  /**
   * GET 请求
   */
  get: <T = any>(endpoint: string, config?: Omit<RequestConfig, 'method'>) => 
    apiClient.get<T>(endpoint, config),
  
  /**
   * POST 请求
   */
  post: <T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>) => 
    apiClient.post<T>(endpoint, data, config),
  
  /**
   * PUT 请求
   */
  put: <T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>) => 
    apiClient.put<T>(endpoint, data, config),
  
  /**
   * DELETE 请求
   */
  delete: <T = any>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) => 
    apiClient.delete<T>(endpoint, config),
  
  /**
   * PATCH 请求
   */
  patch: <T = any>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method' | 'body'>) => 
    apiClient.patch<T>(endpoint, data, config),
  
  /**
   * 文件上传
   */
  upload: <T = any>(endpoint: string, file: File, additionalData?: Record<string, any>) => 
    apiClient.upload<T>(endpoint, file, additionalData),
  
  /**
   * 批量文件上传
   */
  uploadMultiple: <T = any>(endpoint: string, files: File[], additionalData?: Record<string, any>) => 
    apiClient.uploadMultiple<T>(endpoint, files, additionalData)
};

/**
 * 错误处理工具函数
 */
export class ApiError extends Error {
  public code: string;
  public details?: any;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiError';
    this.code = error.code;
    this.details = error.details;
  }
}

/**
 * 检查响应是否成功，如果不成功则抛出错误
 */
export function ensureSuccess<T>(response: ApiResponse<T>): T {
  if (response.success && response.data !== undefined) {
    return response.data;
  }
  
  if (response.error) {
    throw new ApiError(response.error);
  }
  
  throw new Error('请求失败');
}

/**
 * 请求重试工具函数
 */
export async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxAttempts: number = API_CONFIG.RETRY_ATTEMPTS,
  delay: number = API_CONFIG.RETRY_DELAY
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxAttempts) {
        throw lastError;
      }
      
      // 等待一段时间后重试
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw lastError!;
}
